
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { getLoadingMessage, getRetryMessage, LoadingContext } from '@/lib/loading-messages';

interface LoadingStateProps {
  context?: LoadingContext;
  showSkeleton?: boolean;
  skeletonCount?: number;
  className?: string;
  retryAttempt?: number;
  maxRetries?: number;
}

export function LoadingState({
  context = 'default',
  showSkeleton = false,
  skeletonCount = 3,
  className = '',
  retryAttempt = 0,
  maxRetries = 5
}: LoadingStateProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(getLoadingMessage(context, 0));

  useEffect(() => {
    // Rotate messages every 2.5 seconds
    const interval = setInterval(() => {
      setMessageIndex((prev) => prev + 1);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCurrentMessage(getLoadingMessage(context, messageIndex));
  }, [context, messageIndex]);

  return (
    <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
      {/* Skeleton Phase */}
      {showSkeleton && (
        <div className="w-full space-y-3 mb-2">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      )}

      {/* Owl Icon Phase */}
      <div className="text-4xl" style={{ 
        animation: 'owl-blink 3s ease-in-out infinite, owl-gentle-pulse 2s ease-in-out infinite'
      }}>
        🦉
      </div>

      {/* Message Phase */}
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground animate-fade-in-up">
          {currentMessage}
        </p>
        {retryAttempt > 0 && (
          <p className="text-xs text-amber-600 font-medium">
            {getRetryMessage(retryAttempt, maxRetries)}
          </p>
        )}
      </div>
    </div>
  );
}

// Inline variant for small spaces (buttons, forms)
export function LoadingStateInline({ 
  context = 'default',
  className = ''
}: { 
  context?: LoadingContext;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-lg" style={{ animation: 'owl-blink 3s ease-in-out infinite' }}>
        🦉
      </span>
      <span className="text-sm text-muted-foreground">
        {getLoadingMessage(context, 0)}
      </span>
    </div>
  );
}
